import type {
  MonthlyReport,
  TimeEntry,
  User,
  WageCode,
} from "@avg/shared";
import { tStatic } from "./i18n/translations";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

// Names match backend defaults in backend/src/config.ts.
const CSRF_COOKIE = "avg_csrf";
const LEGACY_TOKEN_KEY = "avg.session.token";

// One-time cleanup: previous versions of the app stored a JWT in localStorage.
// Remove it on first import so it can't accidentally leak via XSS.
try {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
} catch {
  // Ignore SSR / blocked-storage errors.
}

function readCookie(name: string): string | null {
  const target = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");

  const method = (init.method ?? "GET").toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      msg = body.error ?? msg;
    } catch {}
    if (res.status === 401) {
      throw new ApiError(401, tStatic("common.session_expired"));
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.blob()) as T;
}

export const api = {
  // === Auth ===
  loginWithGoogle(idToken: string) {
    return request<{ user: User }>("/api/auth/google", {
      method: "POST",
      body: JSON.stringify({ idToken }),
    });
  },
  devLoginEnabled() {
    return request<{ enabled: boolean }>("/api/auth/dev-login");
  },
  devLogin(email: string, name?: string) {
    return request<{ user: User }>("/api/auth/dev-login", {
      method: "POST",
      body: JSON.stringify({ email, name }),
    });
  },
  logout() {
    return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
  },
  me() {
    return request<{ user: User }>("/api/auth/me");
  },

  // === Wage codes ===
  listWageCodes() {
    return request<{ wageCodes: WageCode[] }>("/api/wage-codes");
  },

  // === My reports ===
  getMyReport(year: number, month: number) {
    return request<{ report: MonthlyReport }>(`/api/reports/my?year=${year}&month=${month}`);
  },
  getMyReportList() {
    return request<{ reports: MonthlyReport[] }>(`/api/reports/my/list`);
  },
  patchReport(id: string, data: { noDeviations?: boolean }) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },
  addEntry(reportId: string, entry: Omit<TimeEntry, "id" | "monthlyReportId" | "createdAt">) {
    return request<{ entry: TimeEntry }>(`/api/reports/${reportId}/entries`, {
      method: "POST",
      body: JSON.stringify(entry),
    });
  },
  deleteEntry(reportId: string, entryId: string) {
    return request<{ ok: true }>(`/api/reports/${reportId}/entries/${entryId}`, {
      method: "DELETE",
    });
  },
  submitReport(id: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}/submit`, { method: "POST" });
  },
  recallReport(id: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}/recall`, { method: "POST" });
  },

  // === Manager / Admin ===
  getTeamReports(year: number, month: number) {
    return request<{ reports: MonthlyReport[] }>(
      `/api/reports/team?year=${year}&month=${month}`,
    );
  },
  managerApprove(id: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}/manager-approve`, {
      method: "POST",
    });
  },
  adminApprove(id: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}/admin-approve`, {
      method: "POST",
    });
  },
  rejectReport(id: string, reason: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  getReport(id: string) {
    return request<{ report: MonthlyReport }>(`/api/reports/${id}`);
  },

  // === Admin / users ===
  listUsers() {
    return request<{ users: User[] }>(`/api/admin/users`);
  },
  patchUser(id: string, data: Partial<User>) {
    return request<{ user: User }>(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },
  createUser(data: {
    email: string;
    name: string;
    employeeId?: string | null;
    role?: User["role"];
    managerId?: string | null;
  }) {
    return request<{ user: User }>(`/api/admin/users`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  deleteUser(id: string) {
    return request<void>(`/api/admin/users/${id}`, { method: "DELETE" });
  },

  // === Admin / wage codes ===
  listAllWageCodes() {
    return request<{ wageCodes: WageCode[] }>(`/api/admin/wage-codes`);
  },
  createWageCode(data: WageCode) {
    return request<{ wageCode: WageCode }>(`/api/admin/wage-codes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  patchWageCode(code: string, data: Partial<WageCode>) {
    return request<{ wageCode: WageCode }>(`/api/admin/wage-codes/${code}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  // === Excel export ===
  async downloadExport(year: number, month: number, statuses?: string[]) {
    const qs = new URLSearchParams({ year: String(year), month: String(month) });
    if (statuses?.length) qs.set("includeStatuses", statuses.join(","));
    const res = await fetch(`${API_BASE}/api/admin/export/payroll.xlsx?${qs}`, {
      credentials: "include",
    });
    if (!res.ok) {
      let msg = res.statusText;
      try {
        const body = await res.json();
        msg = body.error ?? msg;
      } catch {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Inlasningsfil_${year}-${String(month).padStart(2, "0")}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export { ApiError };
